// ============================================
// CONFIGURACIÓN DE URL (RAILWAY)
// ============================================
// Si estás en local, comenta la de Railway y descomenta localhost
const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';
// const API_URL = process.env.REACT_APP_API_URL || 'https://luiatro-health-cloud-production.up.railway.app';

// ============================================
// HELPER PARA TOKEN (EVITA ERROR 401)
// ============================================
const getHeaders = () => {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
    };
};

// ============================================
// INTERFACES
// ============================================

export interface Template {
    id: string;
    name: string;
    category: string;
    bodyContent: string;
}

export interface Report {
    id: string;
    title: string;
    reason?: string;
    content: string; 
    createdAt: string;
    updatedAt: string;
    createdBy: string;
    patientId: string;
}

export interface PatientData {
    id: string;
    name: string;
    lastName?: string;
    patientId?: string;
    cedula?: string;
    idNumber?: string;
    age: string;
    gender?: string;
    phone?: string;
    email?: string;
    address?: string;
    birthDate?: string;
    medicalHistory?: any;
}

// ============================================
// FUNCIONES
// ============================================

export const getTemplates = async (): Promise<Template[]> => {
    // Agregado /api
    const response = await fetch(`${API_URL}/api/templates`, {
        headers: getHeaders()
    });
    if (!response.ok) return [];
    const data = await response.json();
    
    return data.map((t: any) => ({
        ...t,
        bodyContent: t.content || ''
    }));
};

export const getPatientReports = async (patientId: string): Promise<Report[]> => {
    // Agregado /api
    const response = await fetch(`${API_URL}/api/reports?patientId=${patientId}`, {
        headers: getHeaders()
    });
    
    if (!response.ok) {
        console.warn(`No se cargaron informes (Status: ${response.status})`);
        return [];
    }

    const data = await response.json();
    
    if (Array.isArray(data)) {
        return data.map((item: any) => ({
            ...item,
            content: item.content || item.bodyContent || '' 
        }));
    } else {
        return [];
    }
};

// ✅ ESTA ES LA FUNCIÓN QUE DABA ERROR DE SINTAXIS - YA CORREGIDA
export const getPatientById = async (patientId: string): Promise<PatientData> => {
    // Agregado /api y corregida la sintaxis del fetch
    const response = await fetch(`${API_URL}/api/patients/${patientId}`, {
        headers: getHeaders()
    });
    
    if (!response.ok) throw new Error('Error al cargar datos del paciente');
    return await response.json();
};

export const saveReport = async (report: any): Promise<Report> => {
    const isEdit = !!report.id;
    // Agregado /api en ambos casos
    const endpoint = isEdit ? `${API_URL}/api/reports/${report.id}` : `${API_URL}/api/reports`;
    const method = isEdit ? 'PUT' : 'POST';
    
    const payload = {
        patientId: report.patientId,
        title: report.title,
        reason: report.reason,
        content: report.content || report.bodyContent,
        headerData: {}, 
        createdBy: report.createdBy
    };
    
    const response = await fetch(endpoint, {
        method,
        headers: getHeaders(),
        body: JSON.stringify(payload)
    });
    
    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Error al guardar el informe');
    }
    
    return await response.json();
};

export const deleteReport = async (reportId: string): Promise<void> => {
    // Agregado /api
    await fetch(`${API_URL}/api/reports/${reportId}`, { 
        method: 'DELETE',
        headers: getHeaders()
    });
};

export const deleteTemplate = async (id: string): Promise<void> => {
    // Agregado /api
    await fetch(`${API_URL}/api/templates/${id}`, { 
        method: 'DELETE',
        headers: getHeaders()
    });
};

export const updateTemplate = async (id: string, data: any): Promise<Template> => {
    const payload = {
        name: data.name,
        title: data.name,
        category: data.category,
        content: data.bodyContent || data.content,
        institutionId: data.institutionId,
        createdBy: data.createdBy
    };

    // Agregado /api
    const response = await fetch(`${API_URL}/api/templates/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(payload)
    });

    if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Error al actualizar plantilla');
    }
    return await response.json();
};

export const toggleWaitingStatus = async (patientId: string, isWaiting: boolean) => {
    const response = await fetch(`${API_URL}/api/patients/${patientId}/waiting`, {
        method: 'PATCH',
        headers: getHeaders(),
        body: JSON.stringify({ isWaiting })
    });
    
    if (!response.ok) {
        throw new Error('Error al cambiar estado de espera');
    }
    
    return await response.json();
};

export const reorderQueue = async (orderedIds: string[]) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/patients/reorder-waiting`, {
        method: 'PUT',
        headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ orderedIds })
    });
    if (!response.ok) throw new Error('Error al reordenar la lista');
    return await response.json();
};